import { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { getObjectWithSelector } from '../../../functions/objectFunctions/getObjectWithSelector';
import { updateObjectNestedProperties } from '../../../functions/objectFunctions/updateObjectNestedProperties';
import { useElements, useScene } from '../../../hooks';
import { LauncherElement } from '../../../types/launcherElement';
import { borderStyleOptions } from '../../../types/options';
import { sizeUnits } from '../../../types/units';
import { BorderBoxInput } from '../inputs/BorderBoxInput';
import { ColorInput } from '../inputs/ColorInput';
import { UnitInput } from '../inputs/UnitInput';
import { SelectInput } from '../inputs/selectInput/SelectInput';

export const BorderBoxInputGroup = ({
  variant,
  tab,
  pseudoclass,

  colorPickerExtended,
  setColorPickerExtended,
}: {
  variant?: string;
  tab?: string;
  pseudoclass?: string;

  colorPickerExtended?: boolean;
  setColorPickerExtended?: (newValue: boolean) => void;
}) => {
  const scene = useScene();
  const lastSelectedElementID = scene.selectedElementIDs.slice(-1)[0];
  const [selectedElement, setSelectedElement] = useElements<LauncherElement>({
    id: lastSelectedElementID,
  });
  const [border, setBorder] = useState<
    'Top' | 'Right' | 'Bottom' | 'Left' | 'All'
  >('All');

  let selector: string = 'props.class';
  if (variant && tab) {
    selector = `props.class.variants.${variant}.${tab}`;
  }

  if (pseudoclass === 'none') pseudoclass = undefined;
  if (pseudoclass) {
    selector = `${selector}.&:${pseudoclass}`;
  }

  let paddingSelector = `padding`;
  let marginSelector = `margin`;
  let borderWidthSelector = `borderWidth`;
  let borderStyleSelector = `borderStyle`;
  let borderColorSelector = `borderColor`;
  if (border !== 'All') {
    paddingSelector = `padding${border}`;
    marginSelector = `margin${border}`;
    borderWidthSelector = `border${border}Width`;
    borderStyleSelector = `border${border}Style`;
    borderColorSelector = `border${border}Color`;
  }

  const styleObj = getObjectWithSelector(selectedElement, selector);

  useEffect(() => {
    setBorder('All');
  }, [lastSelectedElementID]);

  const labelWidth = 45;
  return (
    <Container>
      <BorderBoxInput {...{ border, setBorder }} />
      <InputContainer>
        <UnitInput
          id={`${lastSelectedElementID}-${variant}-padding-${tab}-${pseudoclass}-input`}
          label="Padding"
          value={styleObj?.[paddingSelector]}
          units={sizeUnits}
          defaultUnit="px"
          onChange={(inputValue) =>
            setSelectedElement((prev) =>
              updateObjectNestedProperties(prev, {
                [`${selector}.boxSizing`]: 'border-box',
                [`${selector}.${paddingSelector}`]: inputValue,
              })
            )
          }
          labelWidth={labelWidth}
        />
        <UnitInput
          id={`${lastSelectedElementID}-${variant}-margin-${tab}-${pseudoclass}-input`}
          label="Margin"
          value={styleObj?.[marginSelector]}
          units={sizeUnits}
          defaultUnit="px"
          onChange={(inputValue) =>
            setSelectedElement((prev) =>
              updateObjectNestedProperties(prev, {
                [`${selector}.${marginSelector}`]: inputValue,
              })
            )
          }
          labelWidth={labelWidth}
        />
        <UnitInput
          id={`${lastSelectedElementID}-${variant}-border-width-${tab}-${pseudoclass}-input`}
          label="Border Width"
          value={styleObj?.[borderWidthSelector]}
          units={sizeUnits}
          defaultUnit="px"
          onChange={(inputValue) =>
            setSelectedElement((prev) =>
              updateObjectNestedProperties(prev, {
                [`${selector}.boxSizing`]: 'border-box',
                [`${selector}.${borderWidthSelector}`]: inputValue,
                [`${selector}.${borderStyleSelector}`]:
                  styleObj?.[borderStyleSelector] || 'solid',
              })
            )
          }
          column
        />
        <SelectInput
          options={borderStyleOptions}
          id={`${lastSelectedElementID}-${variant}-border-style-${tab}-${pseudoclass}-input`}
          label="Border Style"
          value={styleObj?.[borderStyleSelector]}
          onChange={(inputValue: string) =>
            setSelectedElement((prev) =>
              updateObjectNestedProperties(prev, {
                [`${selector}.${borderStyleSelector}`]: inputValue,
              })
            )
          }
        />
        <ColorInput
          id={`${lastSelectedElementID}-${variant}-border-color-${tab}-${pseudoclass}-input`}
          label="Border Color"
          value={styleObj?.[borderColorSelector]}
          onChange={(inputValue: string | undefined) =>
            setSelectedElement((prev) =>
              updateObjectNestedProperties(prev, {
                [`${selector}.${borderColorSelector}`]: inputValue,
              })
            )
          }
          column
          extended={colorPickerExtended}
          setExtended={setColorPickerExtended}
        />
      </InputContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const InputContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;
